<template>
  <div
    class="card briefcase-vertical"
    :style="cssVars"
  >
    <div class="card__header-line">
      <div class="card__first-line">
        <div class="fs15">
          {{ $t("rightPanel.briefcase.label") }}
        </div>
      </div>
      <div v-if="!isDataLoadingStatePool">
        <AuSkeleton
          width="170px"
          height="24px"
        />
      </div>

      <div
        v-else
        class="card__second-line"
      >
        <div class="balance">
          <span class="sum">
            <AuDecimalStatic
              :value="fullSum"
              :decimal="2"
              :indent="2"
              :addition="defaultTokenMnemonic"
            />
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="!loading && defaultTokenMnemonic"
      class="card__third-line"
    >
      <div v-if="!balance">
        <AuButton
          type="primary"
          center
          width="100%"
          @click="$router.push(depositUrl)"
        >
          {{ $t("briefcase.buy") }} {{ defaultTokenEnv }}
        </AuButton>
      </div>
      <Apexchart
        v-if="!chartLoading && balance"
        class="chard"
        :options="options"
        :series="mappedSeries"
        width="170px"
        height="340px"
      />
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import CurrencySupport from "@/help/currencySupport";
import { checkAccess, scope } from "@/help/accessData";
import { getDalyBalanceInvest, usersBalances } from "@/help/usersFinances";
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      currencySup: new CurrencySupport(),
      loading: false,
      totalSumInvest: null,
      fullSum: 0,
      balance: 0,
      series: [],
      mappedSeries: [],
      width: null,
      chartLoading: false
    };
  },

  computed: {
    ...mapGetters({
      auOwners: "owner/auOwners",
      myProjectsOrders: "capitals/myProjectsOrders",
      myStackingOrders: "events/myStackingOrders",
      isAssets: "owner/dataLoading",
      tokensFiat: "tokensFiat",
      tokensGold: "tokensGold",
      tokensDao: "tokensDao",
      tokensUtility: "tokensUtility",
      tokens: "tokens",
      defaultTokenMnemonic: "defaultToken/defaultTokenMnemonic",
      exchangeBalances: "exchangeBalances",
      defaultTokenEnv: "defaultToken/defaultTokenEnv",
      dailyStatsBack: "dailyStatsBack",
      checkProfile: "profile/checkProfile"
    }),

    depositUrl() {
      if (checkAccess(scope.notRwa)) {
        return "/my-assets/buy-form";
      }

      return "/my-assets/card-buy-form";
    },

    cssVars() {
      return {
        "--scroll": this.labels?.length < 9 ? "hidden" : "scroll"
      };
    },

    defaultTokenDecimals() {
      return (
        _.find(
          this.tokens,
          (token) => token.mnemonicEng === this.defaultTokenMnemonic
        )?.decimals ?? 2
      );
    },

    isDataLoadingStatePool() {
      return this.auOwners != null;
    },

    legendHeight() {
      if (this.series.length < 9) {
        return "auto";
      }
      return "80";
    },

    isLightTheme() {
      return this.$store.getters.isLightTheme;
    },

    colorLabel() {
      return this.isLightTheme
        ? Array(9).fill("#080808")
        : Array(9).fill("#fff");
    },

    nonAsset() {
      return [
        ...this.tokensFiat,
        ...this.tokensGold,
        this.tokensDao,
        ...this.tokensUtility
      ];
    },

    labels() {
      let resp = [];
      this.series.forEach((series) => {
        const owner = _.find(
          this.auOwners,
          (owner) => owner?.auToken?.id == series.id
        );

        if (owner?.auToken?.mnemonicEng == this.defaultTokenEnv) {
          resp.push(owner?.auToken?.mnemonicEng);
        }
        else {
          resp.push(
            `<span class="legend">${
              this.defaultTokenEnv
            }: <b>${this.convertNumber(
              this.getPrice(owner).mainTokenPrice
            )}</b><br/></span>${owner?.auToken?.mnemonicEng}`
          );
        }
      });
      return resp;
    },

    options: {
      get: function () {
        return {
          labels: this.labels,
          chart: {
            type: "donut"
          },
          pie: {
            expandOnClick: false,
            donut: {
              background: "transparent",
              labels: {
                show: true,
                total: {
                  showAlways: true,
                  show: true
                }
              }
            }
          },
          colors: [
            "#299667",
            "#9720F6",
            "#E2A64D",
            "#489EE1",
            "#294896",
            "#35d5ec",
            "#E2684D",
            "#504DE2",
            "#C3B3D0"
          ],
          dataLabels: {
            enabled: false
          },
          tooltip: {
            enabled: true,
            y: {
              formatter(value) {
                if (value == 0) {
                  return 0;
                }

                value = Math.round(value * 100) / 100;

                let formattedPrice;
                if (value % 1 === 0) {
                  formattedPrice = value
                    .toLocaleString("ru-RU")
                    .replace(",", ".");
                }
                else {
                  formattedPrice = value
                    .toLocaleString("ru-RU", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })
                    .replace(",", ".");
                }
                return formattedPrice;
              }
            }
          },
          legend: {
            show: true,
            position: "bottom",
            horizontalAlign: "center",
            height: this.legendHeight,
            fontSize: "10px",
            fontFamily: "Suisse Intl",
            labels: {
              colors: this.colorLabel
            },
            markers: {
              width: 8,
              height: 8
            }
          }
        };
      },
      set: function (newValue) {
        return newValue;
      }
    }
  },

  watch: {
    auOwners: {
      immediate: true,
      handler() {
        if (this.auOwners) {
          this.checkBalance(this.auOwners);
        }
      }
    },
    defaultTokenMnemonic: {
      immediate: true,
      handler() {
        if (this.auOwners) {
          this.checkBalance(this.auOwners);
        }
      }
    },
    checkProfile: {
      immediate: true,
      async handler() {
        await this.getAuOwners();
        if (this.auOwners) {
          this.checkBalance(this.auOwners);
        }
      }
    }
  },

  async mounted() {
    window.addEventListener("resize", this.resizeHandler);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.resizeHandler);
  },

  methods: {
    ...mapActions({
      getAuOwners: "owner/getAuOwners",
      getWithdrawalRequests: "withdrawal/getWithdrawalRequests"
    }),

    resizeHandler() {
      this.chartLoading = true;
      this.$nextTick(() => {
        this.chartLoading = false;
      });
    },
    changeChart(chartContext, _config) {
      this.$nextTick(() => {
        chartContext.pie.centerX = 75;
      });
    },

    getPrice(owner) {
      let price = owner?.rateDefaultToken || "1";
      let amount = this.currencySup.getBalance(owner?.auToken?.id);

      price = BigNumber(price).multipliedBy(owner?.amount);
      price = price
        .dividedBy(BigNumber(10).pow(owner?.auToken?.decimals))
        .toNumber();

      let mainTokenPrice = BigNumber(amount)
        .dividedBy(BigNumber(10).pow(owner?.auToken?.decimals))
        .toFixed(owner?.auToken?.indent);

      return (price = {
        mainTokenPrice: Number(mainTokenPrice),
        tokenPrice: Number.isNaN(price) ? 0 : price
      });
    },

    convertNumber(price) {
      if (price == 0) {
        return 0;
      }

      price = Math.round(price * 100) / 100;

      let formattedPrice;
      if (price % 1 === 0) {
        formattedPrice = price.toLocaleString("ru-RU").replace(",", ".");
      }
      else {
        formattedPrice = price
          .toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
          .replace(",", ".");
      }
      return formattedPrice;
    },

    checkBalance(value) {
      const { series, mappedSeries, balance } = usersBalances(
        value,
        this.currencySup,
        this.defaultTokenMnemonic
      );

      // забираем балансы и инвестиции с back
      const { sumBalance, sumInvest } = getDalyBalanceInvest(
        this.dailyStatsBack,
        this.defaultTokenDecimals
      );

      this.series = series;
      this.mappedSeries = mappedSeries;
      this.fullSum = parseFloat(sumBalance) + parseFloat(sumInvest);
      this.balance = balance;
    }
  }
};
</script>

<style lang="scss">
.briefcase-vertical {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
  .card__third-line {
    .vue-apexcharts.chard {
      .apexcharts-canvas {
        .apexcharts-svg {
          foreignObject {
            .apexcharts-legend.apx-legend-position-bottom {
              // возможно влияет на prod
              top: 0px !important;
              inset: auto 0px 1px !important;

              display: grid;
              grid-template-columns: 50% auto;
              overflow-y: var(--scroll);
              overflow-x: hidden;
              .apexcharts-legend-series {
                display: flex;
                align-items: center;
                .apexcharts-legend-marker {
                  height: 8px;
                  width: 8px;
                  flex-shrink: 0;
                  svg {
                    border-radius: 50%;
                  }
                }
                .apexcharts-legend-text {
                  padding: 2px 0 2px 2px;
                  margin-left: 0px;
                  .legend {
                    display: none;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.balance {
  display: flex;
  justify-content: center;
  font-weight: 500;
  font-size: 22px;
  line-height: 100%;
  .sum {
    text-align: center;
    line-height: 24px;
  }

  color: #5ac396;

  @media screen and (max-width: 1200px) {
    font-size: 18px;
  }
}

.loader-wrapper {
  height: 100%;
  display: flex;
  align-items: start;
  justify-content: center;
  padding-right: 10px;
}

.card {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84%;

  &__first-line {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    color: var(--text-color);
  }

  &__third-line {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}
</style>
